import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Reveal, PageHero } from './LandingShared';
import { useTranslation } from '../contexts/TranslationContext';
import { DocumentTextIcon, ArrowDownTrayIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { API_BASE_URL } from '../utils/api';

const Literature = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('guides');
  const [guides, setGuides] = useState([]);
  const [forms, setForms] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [loadingForms, setLoadingForms] = useState(true);

  const isHidden = (item) =>
    (item?.displayName || '').trim().toLowerCase() === 'key terms and conditions';

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/public-guides`)
      .then(res => res.json())
      .then(data => setGuides(data.guides || []))
      .catch(err => console.error('Failed to load guides:', err))
      .finally(() => setLoadingGuides(false));

    fetch(`${API_BASE_URL}/api/public-forms`)
      .then(res => res.json())
      .then(data => setForms((data.forms || []).filter(f => !isHidden(f))))
      .catch(err => console.error('Failed to load forms:', err))
      .finally(() => setLoadingForms(false));
  }, []);

  const handleDownloadGuide = (guide) => {
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}${guide.downloadUrl}`;
    link.download = guide.displayName + '.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadForm = (form) => {
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}${form.downloadUrl}`;
    link.download = form.displayName + '.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tabs = [
    { id: 'guides', label: t('literature.guidesTab'), icon: DocumentTextIcon },
    { id: 'forms', label: t('literature.formsTab'), icon: ClipboardDocumentListIcon },
  ];

  const renderItems = (items, loading, emptyMsg, onDownload) => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      );
    }
    if (items.length === 0) {
      return <p className="text-center text-gray-500 py-12">{emptyMsg}</p>;
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {items.map((item, i) => (
          <Reveal key={item.filename} delay={i * 80} className="h-full">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onDownload(item)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDownload(item); } }}
              className="group block w-full h-full text-left rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
            >
              <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 min-w-[48px] min-h-[48px] rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md shrink-0">
                      <DocumentTextIcon className="h-6 w-6 text-white shrink-0" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-lg font-bold text-gray-900">
                        {item.displayName}
                      </h2>
                      <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">PDF</span>
                    </div>
                  </div>
                  <ArrowDownTrayIcon className="h-6 w-6 text-gray-300 group-hover:text-blue-600 group-hover:translate-y-0.5 transition-all duration-300" />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Literature & Guides — Cogent Platform</title>
        <meta name="description" content="Download guides and forms from Cogent Asset Management. Access investment documentation, client guides, and regulatory forms." />
        <link rel="canonical" href="https://cogentplatform.com/guides" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        <PageHero
          title={t('literature.title')}
          subtitle={t('literature.subtitle')}
        />

        <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Tab Bar */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'guides' && renderItems(guides, loadingGuides, 'No guides available at this time.', handleDownloadGuide)}
            {activeTab === 'forms' && renderItems(forms, loadingForms, t('literature.noForms'), handleDownloadForm)}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Literature;
